import { CancelablePromise } from "cancelable-promise";
import {
  JamCanvasAnimate,
  JamCanvasDrawerStatus,
  JamCanvasStatus,
  JamTileImageProps,
} from "../types.js";
import { getCanvasContext, getSizedCanvas, resizeCanvas } from "./canvas.js";
import { ANIMATION_STEPS } from "../const.js";
import { Observable } from "./observable.js";

export class CanvasDrawer {
  observable: Observable<{ status?: JamCanvasDrawerStatus }> = new Observable(
    {}
  );
  animations: CancelablePromise[] = [];
  canvas: HTMLCanvasElement;
  currentStatus?: JamCanvasStatus;
  previousStatus?: JamCanvasStatus;

  constructor(canvas = document.createElement("canvas")) {
    this.canvas = canvas;
  }

  next(currentStatus?: JamCanvasStatus) {
    const { currentStatus: previousStatus } = this;

    this.currentStatus = currentStatus && { ...currentStatus };
    this.previousStatus = previousStatus;

    this.cancelAnimations();
    return this;
  }

  cancelAnimations() {
    for (const promise of this.animations) {
      !promise.isCanceled() && promise.cancel();
    }
    this.animations = [];
    return this;
  }

  addAnimation(promise: CancelablePromise) {
    this.animations.push(promise.finally(() => this.removeAnimation(promise)));
    return this;
  }

  removeAnimation(promise: CancelablePromise) {
    this.animations = this.animations.filter((item) => item !== promise);
    return this;
  }

  destroy() {
    this.observable.destroy();
    this.cancelAnimations();
  }

  clear() {
    const context = getCanvasContext({ canvas: this.canvas });
    const { width, height } = this.canvas;
    context.clearRect(0, 0, width, height);
  }

  static resetCanvas(canvasDrawer: CanvasDrawer) {
    const { currentStatus, previousStatus, canvas } = canvasDrawer;
    if (!currentStatus) return;
    const { width, height, scale, x, y, bgColor } = currentStatus;

    const context = getCanvasContext({ canvas });

    const resizeParams = {
      canvas,
      ctx: context,
      width,
      height,
      bgColor,
    };

    if (!previousStatus) return resizeCanvas(resizeParams);

    const scaleDiff = previousStatus.scale / scale;

    const xDiff = previousStatus.x - x;
    const yDiff = previousStatus.y - y;

    const dx = xDiff / scale;
    const dy = yDiff / scale;

    const dw = previousStatus.width * scaleDiff;
    const dh = previousStatus.height * scaleDiff;

    resizeCanvas({
      ...resizeParams,
      bgColor,
      dx,
      dy,
      dw,
      dh,
    });
  }

  static getTileImage(args: {
    tile: JamTileImageProps;
    canvas: HTMLCanvasElement;
    options?: CanvasRenderingContext2DSettings;
  }) {
    const { tile, canvas, options } = args;
    if (!tile.img) return;
    const tileCanvas = getSizedCanvas(tile.img);
    const tileContext = getCanvasContext({
      canvas: tileCanvas,
      options,
    });

    tileContext.drawImage(
      canvas,
      tile.x * devicePixelRatio,
      tile.y * devicePixelRatio,
      tile.img.width * devicePixelRatio,
      tile.img.height * devicePixelRatio,
      0,
      0,
      tile.img.width,
      tile.img.height
    );
    return tileCanvas;
  }

  static setTileAlpha(args: {
    img: ImageBitmap | HTMLImageElement;
    canvas: HTMLCanvasElement;
    alpha: number;
    previousImage?: CanvasImageSource;
  }) {
    const { img, alpha, canvas, previousImage } = args;

    const context = getCanvasContext({ canvas });

    context.clearRect(0, 0, canvas.width, canvas.height);

    context.globalAlpha = 1;
    context.drawImage(img, 0, 0);

    if (previousImage) {
      context.globalAlpha = 1 - alpha;
      context.drawImage(previousImage, 0, 0);
    }
  }

  static drawJamCanvas(args: {
    canvasDrawer: CanvasDrawer;
    tiles: JamTileImageProps[];
    count: number;
    total: number;
    reset?: boolean;
    animate?: JamCanvasAnimate;
  }) {
    const { canvasDrawer, tiles, count, total, reset, animate } = args;

    let animationSteps = animate === true ? ANIMATION_STEPS : animate || 1;
    const stepSize = animate ? 1 / animationSteps : 1;

    const animation = new CancelablePromise((resolve, _, onCancel) => {
      let cancelled = false;

      onCancel(() => {
        canvasDrawer.removeAnimation(animation);
        cancelled = true;
      });

      const { canvas } = canvasDrawer;
      const context = getCanvasContext({ canvas });
      reset && CanvasDrawer.resetCanvas(canvasDrawer);

      for (const tile of tiles) {
        if (!tile.img) continue;
        const previousImage =
          animationSteps > 1
            ? CanvasDrawer.getTileImage({ tile, canvas })
            : undefined;

        const tileCanvas = getSizedCanvas(tile.img);

        let alpha = 0;
        function loop() {
          if (cancelled || !tile.img) return;

          alpha = parseFloat(Math.min(alpha + stepSize, 1).toFixed(2));

          CanvasDrawer.setTileAlpha({
            img: tile.img,
            alpha,
            canvas: tileCanvas,
            previousImage,
          });

          context.clearRect(
            tile.x,
            tile.y,
            tileCanvas.width,
            tileCanvas.height
          );

          context.drawImage(tileCanvas, tile.x, tile.y);
          canvasDrawer.observable.setValue("status", {
            count,
            total,
            alpha,
          });

          if (alpha >= 1) return resolve(undefined);
          requestAnimationFrame(loop);
        }
        requestAnimationFrame(loop);
      }
    });
    canvasDrawer.addAnimation(animation);
  }
}
